<template>
  <FormGroup :label="processedLabel">
    <template v-if="integratedPartner">
      <InputText
        v-if="isUnstructured"
        v-model="model"
        size="large"
        class="w-full"
      />
      <div
        v-else
        class="flex items-center gap-2"
      >
        <Select
          v-model="model"
          filter
          filter-placeholder="Search..."
          :options="fields"
          :loading="isLoading"
          option-label="label"
          option-group-label="label"
          option-group-children="items"
          option-value="value"
          :show-clear="canClear"
          class="w-full"
          :placeholder="placeholder"
          :disabled="isLoading"
        />
        <Button
          v-if="canCreateCustomField"
          label="New"
          icon="pi pi-plus"
          outlined
          @click="showCreateDialog = true"
        />
      </div>
    </template>
    <template v-else>
      <div class="rounded bg-white px-3 py-2 text-brand-text ring ring-1 ring-surface-900/10">
        No integration available
      </div>
    </template>
  </FormGroup>
  
  <CreateCustomFieldDialog 
    v-model="showCreateDialog" 
    @created="handleFieldCreated"
  />
</template>

<script setup lang="ts">
import { useIntegrationDataStore } from '@/modules/integration/stores/integrationDataStore'
import CreateCustomFieldDialog from './CreateCustomFieldDialog.vue'

const props = defineProps({
  label: { type: [String, Boolean], default: '$FIELD' },
  prefix: { type: Boolean, default: true },
  includeNative: { type: Boolean, default: true },
  placeholder: { type: String },
  canClear: { type: Boolean, default: true }
})
const model = defineModel()

const showCreateDialog = ref(false)

const integrationStore = useIntegrationStore()
const { integratedPartner } = storeToRefs(integrationStore)
const integrationDataStore = useIntegrationDataStore()

const isUnstructured = computed(() => {
  const customField = integratedPartner.value?.espFeatureSupport?.customField
  return customField && typeof customField === 'object' && 'unstructured' in customField
    ? customField.unstructured
    : false
})

const canCreateCustomField = computed(() => {
  const customField = integratedPartner.value?.espFeatureSupport?.customField
  return (
    customField &&
    typeof customField === 'object' &&
    customField.create === true &&
    !customField.unstructured // If unstructured is not set, it defaults to false (structured)
  )
})

// Reactive data that automatically fetches when needed
const nativeFieldsData = ref<any[]>([])
const customFieldsData = ref<any[]>([])
const isLoading = ref(false)

// Fetch data when integrated partner is available
watchEffect(async () => {
  if (integratedPartner.value) {
    isLoading.value = true
    try {
      // Fetch both in parallel
      const [nativeFields, customFields] = await Promise.all([
        props.includeNative ? integrationDataStore.getNativeFields() : [],
        integrationDataStore.getCustomFields()
      ])
      
      nativeFieldsData.value = nativeFields
      customFieldsData.value = customFields
    } catch (error) {
      console.error('Error fetching integration fields:', error)
      nativeFieldsData.value = []
      customFieldsData.value = []
    } finally {
      isLoading.value = false
    }
  } else {
    nativeFieldsData.value = []
    customFieldsData.value = []
    isLoading.value = false
  }
})

const availableNativeFields = computed(() => {
  if (!props.includeNative) return []
  return nativeFieldsData.value.map(
    (field: any) => ({
      label: field.label,
      value: props.prefix ? `native*${field.id}` : String(field.id)
    })
  )
})

const availableCustomFields = computed(() => {
  return customFieldsData.value.map(
    (field: any) => ({
      label: field.label,
      value: props.prefix ? `custom*${field.id}` : String(field.id)
    })
  )
})

const fields = computed(() => {
  return [
    ...(props.includeNative
      ? [
        {
          label: 'Native Fields',
          items: availableNativeFields.value
        }
      ]
      : []),
    {
      label: 'Custom Fields',
      items: availableCustomFields.value
    }
  ]
})

const processedLabel = computed(() => {
  if (props.label === false) {
    return false
  }
  let field = 'Field'
  const customField = integratedPartner.value?.espFeatureSupport?.customField
  if (customField && typeof customField === 'object' && 'label' in customField) {
    field = customField.label
  }
  if (typeof props.label === 'string') {
    return props.label.replace('$FIELD', field)
  }
  return props.label
})

const handleFieldCreated = async (fieldId: string) => {
  // Set the model value to the new field's ID
  model.value = props.prefix ? `custom*${fieldId}` : fieldId
  
  // Refetch custom fields to update the dropdown
  const updatedFields = await integrationDataStore.getCustomFields()
  customFieldsData.value = updatedFields
}
</script>
